import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui/sheet";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  Search,
  Loader2,
  Info,
  ArrowLeft,
  CalendarCheck,
  FileText,
  RotateCcw,
} from "lucide-react";
import { format, parse } from "date-fns";
import { toast } from "sonner";
import { getErrorMessage } from "@/shared/utils/commonUtils";
import {
  authService,
  isAppointmentTrackResult,
  type TrackStatusReferenceType,
  type TrackStatusResponse,
} from "@/features/auth/api/auth.service";
import { resolveStatusDisplay } from "@/shared/utils/trackStatusDisplay";

export interface PublicTrackStatusSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PublicTrackStatusSheet({
  open,
  onOpenChange,
}: PublicTrackStatusSheetProps) {
  const { t } = useTranslation("common");

  const [step, setStep] = useState<"form" | "result">("form");
  const [referenceType, setReferenceType] = useState<TrackStatusReferenceType>("appointment");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [passportNumber, setPassportNumber] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TrackStatusResponse | null>(null);

  useEffect(() => {
    if (!open) {
      setStep("form");
      setReferenceNumber("");
      setPassportNumber("");
      setError("");
      setLoading(false);
      setResult(null);
      setReferenceType("appointment");
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const refTrimmed = referenceNumber.trim();
    const ppTrimmed = passportNumber.trim();

    if (!refTrimmed) {
      setError(t("publicTrack.referenceRequired"));
      return;
    }
    if (!ppTrimmed) {
      setError(t("publicTrack.passportRequired"));
      return;
    }

    setLoading(true);
    try {
      const data = await authService.getPublicTrackStatus({
        referenceType,
        referenceNumber: refTrimmed,
        passportNumber: ppTrimmed,
      });
      setResult(data);
      setStep("result");
    } catch (err: unknown) {
      const message = getErrorMessage(err, t("publicTrack.genericError"));
      toast.error(t("publicTrack.lookupFailed"), { description: message });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep("form");
    setResult(null);
    setError("");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex flex-col overflow-hidden p-0 w-full sm:w-[50vw] sm:max-w-[50vw] max-h-[100dvh]"
      >
        {step === "form" ? (
          <>
            <SheetHeader className="px-4 sm:px-6 pt-[max(0.75rem,env(safe-area-inset-top))] py-3 sm:py-4 border-b border-gray-100">
              <SheetTitle className="text-lg sm:text-[22px] font-bold leading-7 tracking-[-0.44px] text-[#121926]">
                {t("publicTrack.title")}
              </SheetTitle>
            </SheetHeader>

            <div className="flex flex-1 flex-col overflow-y-auto px-4 sm:px-6 pt-0 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <p className="text-sm text-gray-600 mb-6">
                {t("publicTrack.subtitle")}
              </p>

              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                {/* Reference type */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium text-gray-700">
                    {t("publicTrack.referenceType")} <span className="text-red-500">*</span>
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    {([
                      { value: "appointment" as const, label: t("publicTrack.typeAppointment"), Icon: CalendarCheck },
                      { value: "application" as const, label: t("publicTrack.typeApplication"), Icon: FileText },
                    ]).map(({ value, label, Icon }) => {
                      const isSelected = referenceType === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            setReferenceType(value);
                            setReferenceNumber("");
                            setPassportNumber("");
                            setError("");
                          }}
                          className={`flex items-center justify-center gap-2.5 h-11 px-4 rounded-lg border transition-colors ${isSelected
                              ? "border-[#2e5784] bg-[#2e5784]/10 text-[#121926]"
                              : "border-gray-200 bg-white text-gray-500 hover:border-[#2e5784]/40 hover:text-[#121926]"
                            }`}
                        >
                          <Icon className={`w-4 h-4 shrink-0 ${isSelected ? "text-[#2e5784]" : "text-gray-400"}`} />
                          <span className="text-sm font-medium">{label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Reference number */}
                <div className="space-y-2">
                  <Label htmlFor="pub-track-ref" className="text-sm font-medium text-gray-700">
                    {referenceType === "appointment"
                      ? t("publicTrack.appointmentNumber")
                      : t("publicTrack.referenceNumber")} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="pub-track-ref"
                    type="text"
                    placeholder={
                      referenceType === "appointment"
                        ? t("publicTrack.refPlaceholderAppointment")
                        : t("publicTrack.refPlaceholderApplication")
                    }
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    className="h-11 rounded-lg border-gray-300"
                  />
                  <p className="flex items-start gap-2 text-xs text-gray-500">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      {referenceType === "appointment"
                        ? t("publicTrack.refHintAppointment")
                        : t("publicTrack.refHintApplication")}
                    </span>
                  </p>
                </div>

                {/* Passport number */}
                <div className="space-y-2">
                  <Label htmlFor="pub-track-passport" className="text-sm font-medium text-gray-700">
                    {t("publicTrack.passportNumber")} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="pub-track-passport"
                    type="text"
                    placeholder={t("publicTrack.passportPlaceholder")}
                    value={passportNumber}
                    onChange={(e) => setPassportNumber(e.target.value)}
                    className="h-11 rounded-lg border-gray-300"
                  />
                </div>

                {error && (
                  <p className="text-sm text-red-600">{error}</p>
                )}

                <Button
                  type="submit"
                  disabled={loading}
                  className="min-h-[44px] h-11 w-full sm:w-auto rounded-lg bg-[#2e5784] hover:bg-[#2e5784]/90 px-8"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t("publicTrack.checking")}
                    </>
                  ) : (
                    <>
                      <Search className="mr-2 h-4 w-4" />
                      {t("publicTrack.viewStatus")}
                    </>
                  )}
                </Button>
              </form>
            </div>
          </>
        ) : (
          <>
            <SheetHeader className="px-4 sm:px-6 pt-[max(0.75rem,env(safe-area-inset-top))] py-3 sm:py-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                  aria-label={t("buttons.back")}
                >
                  <ArrowLeft className="h-4 w-4 text-gray-600" />
                </button>
                <SheetTitle className="text-lg sm:text-[22px] font-bold leading-7 tracking-[-0.44px] text-[#121926]">
                  {t("publicTrack.resultTitle")}
                </SheetTitle>
              </div>
            </SheetHeader>

            <div className="flex flex-1 flex-col overflow-y-auto">
              {/* Reference info banner — full bleed */}
              <div className="flex items-center justify-between gap-4 bg-[#2e5784] px-5 sm:px-7 py-5">
                <div className="flex flex-col gap-1 min-w-0">
                  <p className="text-[11px] font-medium uppercase tracking-wider text-white/60">
                    {referenceType === "appointment"
                      ? t("publicTrack.appointmentNumber")
                      : t("publicTrack.referenceNumber")}
                  </p>
                  <p className="text-lg font-bold text-white tabular-nums tracking-wide">{referenceNumber.trim()}</p>
                </div>
                <span className="shrink-0 inline-flex items-center rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 text-[11px] font-semibold text-white uppercase tracking-wider">
                  {referenceType === "appointment"
                    ? t("publicTrack.typeAppointment")
                    : t("publicTrack.typeApplication")}
                </span>
              </div>

              <div className="flex flex-1 flex-col gap-5 px-5 sm:px-7 py-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
                {result && isAppointmentTrackResult(result) ? (
                  <AppointmentResult data={result} />
                ) : result ? (
                  <ApplicationResult status={(result as { status: string }).status} />
                ) : null}

                <Button
                  variant="outline"
                  onClick={handleBack}
                  className="min-h-[46px] h-[46px] rounded-xl border-[#2e5784]/20 text-[#2e5784] hover:bg-[#2e5784]/5 hover:border-[#2e5784]/40 font-semibold gap-2 mt-auto"
                >
                  <RotateCcw className="h-4 w-4" />
                  {t("publicTrack.searchAgain")}
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function ApplicationResult({ status }: { status: string }) {
  const { t } = useTranslation("common");
  const { t: tAppt } = useTranslation("appointments");
  const { title, description, icon: Icon } = resolveStatusDisplay(status, tAppt);

  return (
    <div className="flex flex-col gap-4 sm:gap-5 rounded-xl border border-gray-200 bg-white p-5 sm:p-6 shadow-sm">
      <h3 className="text-xs sm:text-sm font-bold uppercase tracking-widest text-gray-400">
        {t("publicTrack.currentStatus")}
      </h3>
      <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4 sm:p-5">
        <div className="flex items-start gap-3.5 sm:gap-4">
          <div className="flex h-10 w-10 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-full bg-blue-100">
            <Icon className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
          </div>
          <div className="flex flex-1 flex-col gap-1 sm:gap-1.5 min-w-0">
            <p className="text-base sm:text-lg font-bold leading-6 sm:leading-7 text-blue-800">{title}</p>
            <p className="text-sm sm:text-[15px] font-normal leading-5 sm:leading-6 text-gray-600">{description}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function getApptStatusLabel(status: string, t: (key: string) => string): string {
  const map: Record<string, string> = {
    DRAFT: "manage.statusDraft",
    SLOT_CONFIRMED: "manage.statusSlotConfirmed",
    BOOKED: "manage.statusBooked",
    COMPLETED: "manage.statusCompleted",
    CANCELLED: "manage.statusCancelled",
    NO_SHOW: "manage.statusNoShow",
    PAYMENT_FAILED: "manage.statusPaymentFailed",
    PAYMENT_INITIATED: "manage.statusPaymentInitiated",
  };
  const key = map[status];
  return key ? t(key) : status;
}

function getApptStatusStyle(status: string): string {
  switch (status) {
    case "COMPLETED":
      return "bg-green-50 border-green-200 text-green-700";
    case "CANCELLED":
      return "bg-red-50 border-red-200 text-red-700";
    case "DRAFT":
      return "bg-gray-100 border-gray-200 text-gray-700";
    case "NO_SHOW":
    case "PAYMENT_FAILED":
      return "bg-amber-50 border-amber-200 text-amber-700";
    default:
      return "bg-blue-50 border-blue-200 text-blue-700";
  }
}

function parseApptDateTime(raw: string) {
  try {
    const dt = parse(raw, "yyyy-MM-dd HH:mm", new Date());
    return {
      dayShort: format(dt, "EEE"),
      dayNumber: format(dt, "d"),
      monthShort: format(dt, "MMM"),
      year: format(dt, "yyyy"),
      timeLabel: format(dt, "hh:mm a"),
    };
  } catch {
    return null;
  }
}

function AppointmentResult({
  data,
}: {
  data: { participants: Array<{ participantName: string; appointmentDateTime: string; appointmentStatus: string }> };
}) {
  const { t } = useTranslation("common");
  const { t: tAppt } = useTranslation("appointments");

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
        {t("publicTrack.appointmentDetails")}
      </h3>
      <div className="flex flex-col gap-4">
        {data.participants.map((p, idx) => {
          const dt = parseApptDateTime(p.appointmentDateTime);
          const statusLabel = getApptStatusLabel(p.appointmentStatus, tAppt);
          const statusStyle = getApptStatusStyle(p.appointmentStatus);

          return (
            <div
              key={idx}
              className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
            >
              {/* Participant header */}
              <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-gray-100 bg-gray-50/50">
                <p className="text-[15px] font-bold leading-6 text-[#121926]">
                  {p.participantName}
                </p>
                <span className={`shrink-0 inline-flex items-center rounded-full border px-3 py-0.5 text-xs font-semibold ${statusStyle}`}>
                  {statusLabel}
                </span>
              </div>
              {/* Date + time row */}
              {dt && (
                <div className="flex items-center gap-4 px-5 py-4">
                  <div className="flex shrink-0 flex-col shadow-sm rounded-[10px] overflow-hidden">
                    <div className="flex flex-col items-center justify-center bg-[#2e5784] px-4 pt-2 pb-1">
                      <p className="text-[10px] font-medium leading-4 text-white/70">{dt.dayShort}</p>
                      <p className="text-sm font-bold leading-5 text-white">{dt.monthShort} {dt.dayNumber}</p>
                    </div>
                    <div className="flex flex-col items-center justify-center border border-t-0 border-gray-200 bg-white px-4 pb-2 pt-1">
                      <p className="text-xs font-semibold leading-4 text-gray-500">{dt.year}</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">{t("publicTrack.scheduledAt")}</p>
                    <p className="text-base font-semibold text-[#121926]">{dt.timeLabel}</p>
                    <p className="text-xs text-gray-500">{dt.dayShort}, {dt.monthShort} {dt.dayNumber} {dt.year}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
